import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSite } from "@/context/SiteContext";

function injectHtml(html, target, position, marker) {
  if (!html?.trim() || document.querySelector(`[data-injected="${marker}"]`)) return;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const nodes = [...doc.head.childNodes, ...doc.body.childNodes];
  const wrapper = document.createDocumentFragment();
  nodes.forEach((node) => {
    if (node.nodeName === "SCRIPT") {
      const s = document.createElement("script");
      [...node.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.textContent = node.textContent;
      s.dataset.injected = marker;
      wrapper.appendChild(s);
    } else {
      if (node.nodeType === 1) node.dataset.injected = marker;
      wrapper.appendChild(document.adoptNode(node));
    }
  });
  const first = document.createElement("meta");
  first.dataset.injected = marker;
  wrapper.prepend(first);
  position === "start" ? target.prepend(wrapper) : target.appendChild(wrapper);
}

function loadScript(src, marker) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  s.dataset.injected = marker;
  document.head.appendChild(s);
}

export function Scripts() {
  const { content } = useSite();
  const { pathname } = useLocation();
  const seo = content.seo;
  const done = useRef(false);
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => {
    if (done.current || isAdmin || !content.loaded) return;
    done.current = true;

    const ga = seo.google_analytics_id?.trim();
    if (ga) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", ga, { send_page_view: false });
      loadScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga)}`, "ga4");
    }

    const gtm = seo.google_tag_manager_id?.trim();
    if (gtm) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
      loadScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtm)}`, "gtm");
      const ns = document.createElement("noscript");
      ns.dataset.injected = "gtm";
      ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtm}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.prepend(ns);
    }

    injectHtml(seo.head_html, document.head, "end", "head-html");
    injectHtml(seo.body_start_html, document.body, "start", "body-html");
    injectHtml(seo.footer_html, document.body, "end", "footer-html");
  }, [content.loaded, seo, isAdmin]);

  useEffect(() => {
    if (isAdmin || !window.gtag || !seo.google_analytics_id?.trim()) return;
    window.gtag("event", "page_view", { page_path: pathname, page_title: document.title, page_location: window.location.href });
  }, [pathname, isAdmin, seo.google_analytics_id]);

  return null;
}
